/**
 * Polygon Nesting Utilities
 * Based on SVGnest's approach using No-Fit Polygon (NFP) for efficient packing
 * https://github.com/Jack000/SVGnest
 */

import ClipperLib from 'clipper-lib';

// Clipper uses integer coordinates, so we scale floats
const CLIPPER_SCALE = 10000;

// =============================================================================
// GEOMETRY UTILITIES
// =============================================================================

/**
 * Calculate polygon area using shoelace formula
 * Negative = counter-clockwise, Positive = clockwise
 */
export const polygonArea = (polygon) => {
  let area = 0;
  const n = polygon.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += polygon[i].x * polygon[j].y;
    area -= polygon[j].x * polygon[i].y;
  }
  return area / 2;
};

/**
 * Get axis-aligned bounding box of polygon
 */
export const getPolygonBounds = (polygon) => {
  if (!polygon || polygon.length === 0) return null;

  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;

  for (const p of polygon) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
};

/**
 * Check if point is inside polygon using ray casting
 * Returns: true (inside), false (outside), null (on edge)
 */
export const pointInPolygon = (point, polygon) => {
  if (!polygon || polygon.length < 3) return false;

  let inside = false;
  const n = polygon.length;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;

    // Check if point is on edge
    const onSegment = (yi - point.y) * (xj - xi) === (yj - yi) * (xi - point.x) &&
                      Math.min(xi, xj) <= point.x && point.x <= Math.max(xi, xj) &&
                      Math.min(yi, yj) <= point.y && point.y <= Math.max(yi, yj);
    if (onSegment) return null;

    // Ray casting
    if (((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
};

/**
 * Rotate polygon by angle (degrees) around origin
 */
export const rotatePolygon = (polygon, angleDeg) => {
  const angleRad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);

  return polygon.map(p => ({
    x: p.x * cos - p.y * sin,
    y: p.x * sin + p.y * cos
  }));
};

/**
 * Translate polygon by offset
 */
export const translatePolygon = (polygon, dx, dy) => {
  return polygon.map(p => ({ x: p.x + dx, y: p.y + dy }));
};

/**
 * Normalize polygon to have bounding box at (0, 0)
 */
export const normalizePolygon = (polygon) => {
  const bounds = getPolygonBounds(polygon);
  if (!bounds) return { polygon, width: 0, height: 0 };

  return {
    polygon: translatePolygon(polygon, -bounds.x, -bounds.y),
    width: bounds.width,
    height: bounds.height
  };
};

/**
 * Convert polygon to Clipper format (scaled integers)
 */
const toClipperPolygon = (polygon) => {
  return polygon.map(p => ({
    X: Math.round(p.x * CLIPPER_SCALE),
    Y: Math.round(p.y * CLIPPER_SCALE)
  }));
};

/**
 * Convert Clipper polygon back to our format
 */
const fromClipperPolygon = (clipperPoly) => {
  return clipperPoly.map(p => ({
    x: p.X / CLIPPER_SCALE,
    y: p.Y / CLIPPER_SCALE
  }));
};

// =============================================================================
// NO-FIT POLYGON (NFP) CALCULATION
// =============================================================================

/**
 * Calculate No-Fit Polygon using Minkowski sum
 * The NFP represents all positions where polygon B can be placed
 * such that it touches but doesn't intersect polygon A
 *
 * @param {Array} A - Stationary polygon
 * @param {Array} B - Orbiting polygon (will be placed relative to NFP)
 * @param {boolean} inside - If true, B orbits inside A (for bin boundary)
 * @returns {Array} - NFP polygon(s)
 */
export const noFitPolygon = (A, B, inside = false) => {
  // Ensure polygons have correct winding (A clockwise, B counter-clockwise for outer NFP)
  const areaA = polygonArea(A);
  const areaB = polygonArea(B);

  let polyA = areaA < 0 ? [...A].reverse() : A;
  let polyB = areaB > 0 ? [...B].reverse() : B;

  // For Minkowski difference, we negate B
  const negB = polyB.map(p => ({ x: -p.x, y: -p.y }));

  // Convert to Clipper format
  const clipperA = toClipperPolygon(polyA);
  const clipperNegB = toClipperPolygon(negB);

  // Calculate Minkowski sum (A + (-B) = A - B)
  const solution = ClipperLib.Clipper.MinkowskiSum(clipperA, clipperNegB, true);

  if (!solution || solution.length === 0) {
    return null;
  }

  // Convert back and return the largest polygon (outer NFP)
  const results = solution.map(fromClipperPolygon);

  // For inside NFP (bin boundary), we want the inner contour
  if (inside) {
    // Find the polygon with positive area (hole)
    for (const poly of results) {
      if (polygonArea(poly) > 0) {
        return poly;
      }
    }
  }

  // For outside NFP (part-to-part), return largest by area
  let largest = results[0];
  let largestArea = Math.abs(polygonArea(largest));

  for (let i = 1; i < results.length; i++) {
    const area = Math.abs(polygonArea(results[i]));
    if (area > largestArea) {
      largest = results[i];
      largestArea = area;
    }
  }

  return largest;
};

/**
 * Calculate Inner Fit Polygon - where a piece can be placed inside a bin
 * @param {Array} bin - The container polygon (usually rectangle)
 * @param {Array} piece - The piece to place
 * @returns {Array} - IFP polygon representing valid placement positions
 */
export const innerFitPolygon = (bin, piece) => {
  // The IFP is the Minkowski difference of bin and piece
  // But with piece at its reference point (usually bottom-left corner)
  const bounds = getPolygonBounds(piece);

  // Translate piece so reference point is at origin
  const normalizedPiece = translatePolygon(piece, -bounds.x, -bounds.y);

  // Calculate NFP with inside=true
  return noFitPolygon(bin, normalizedPiece, true);
};

// =============================================================================
// POLYGON INTERSECTION/COLLISION
// =============================================================================

/**
 * Check if two line segments intersect
 */
const lineIntersect = (p1, p2, p3, p4) => {
  const denom = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
  if (Math.abs(denom) < 1e-10) return null;

  const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / denom;
  const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / denom;

  if (ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1) {
    return {
      x: p1.x + ua * (p2.x - p1.x),
      y: p1.y + ua * (p2.y - p1.y)
    };
  }
  return null;
};

/**
 * Check if two polygons intersect
 */
export const polygonsIntersect = (A, B) => {
  // Check edge intersections
  for (let i = 0; i < A.length; i++) {
    const a1 = A[i];
    const a2 = A[(i + 1) % A.length];

    for (let j = 0; j < B.length; j++) {
      const b1 = B[j];
      const b2 = B[(j + 1) % B.length];

      if (lineIntersect(a1, a2, b1, b2)) {
        return true;
      }
    }
  }

  // Check if one polygon is inside the other
  if (pointInPolygon(A[0], B) === true || pointInPolygon(B[0], A) === true) {
    return true;
  }

  return false;
};

// =============================================================================
// NFP-BASED PLACEMENT
// =============================================================================

/**
 * Find optimal placement position on NFP
 * Uses bottom-left heuristic - finds the lowest, then leftmost valid position
 */
const findPlacementOnNFP = (nfp, placedNFPs, binBounds) => {
  if (!nfp || nfp.length === 0) return null;

  // Find the bottom-left point on the NFP that doesn't intersect with placed NFPs
  let bestPoint = null;

  // Sample points along NFP edges
  const samplePoints = [];
  for (let i = 0; i < nfp.length; i++) {
    const p1 = nfp[i];
    const p2 = nfp[(i + 1) % nfp.length];

    // Add vertex
    samplePoints.push(p1);

    // Add midpoint
    samplePoints.push({
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2
    });
  }

  // Sort by y (ascending), then x (ascending) for bottom-left preference
  samplePoints.sort((a, b) => {
    if (Math.abs(a.y - b.y) < 0.001) return a.x - b.x;
    return a.y - b.y;
  });

  // Find first valid point
  for (const point of samplePoints) {
    // Check if point is within bin bounds
    if (point.x < binBounds.x || point.y < binBounds.y ||
        point.x > binBounds.x + binBounds.width ||
        point.y > binBounds.y + binBounds.height) {
      continue;
    }

    // Check if point is inside all placed NFPs (meaning collision would occur)
    let valid = true;
    for (const placedNFP of placedNFPs) {
      if (pointInPolygon(point, placedNFP) === true) {
        valid = false;
        break;
      }
    }

    if (valid) {
      bestPoint = point;
      break;
    }
  }

  return bestPoint;
};

/**
 * Get rectangle vertices from dimensions
 */
export const getRectangleVertices = (width, height) => [
  { x: 0, y: 0 },
  { x: width, y: 0 },
  { x: width, y: height },
  { x: 0, y: height }
];

/**
 * Get piece polygon (either from vertices or as rectangle)
 */
export const getPiecePolygon = (piece) => {
  if (piece.vertices && piece.vertices.length >= 3) {
    return piece.vertices;
  }
  return getRectangleVertices(piece.width, piece.height);
};

// =============================================================================
// MAIN NESTING ALGORITHM
// =============================================================================

/**
 * NFP-based polygon nesting algorithm
 * Places irregular shapes efficiently using grid-based placement with collision detection
 *
 * Key features:
 * - Tests ALL allowed rotations to find optimal fit (important for trapezoid interlocking)
 * - Never gives up on pieces - creates new sheets as needed
 * - Sorts by area (largest first) for better bin packing
 *
 * @param {Array} pieces - Array of pieces with width, height, and optional vertices
 * @param {number} binWidth - Sheet/bin width
 * @param {number} binHeight - Sheet/bin height
 * @param {number} spacing - Minimum spacing between pieces (kerf/cut width)
 * @param {Object} options - Additional options
 * @returns {Object} - { layouts, unplacedPieces, unplacedCount }
 */
export const nestPolygons = (pieces, binWidth, binHeight, spacing = 0, options = {}) => {
  const {
    rotations = [0, 90, 180, 270] // Default rotation angles to try
  } = options;

  console.log(`Polygon nesting: ${pieces.length} pieces, bin ${binWidth}x${binHeight}, spacing ${spacing}`);

  // Sort pieces by area (largest first) for better packing
  const sortedPieces = [...pieces].sort((a, b) => {
    const areaA = a.width * a.height;
    const areaB = b.width * b.height;
    return areaB - areaA;
  });

  const layouts = [];
  let remainingPieces = [...sortedPieces];
  let maxIterations = remainingPieces.length + 10; // Safety limit
  let iteration = 0;

  while (remainingPieces.length > 0 && iteration < maxIterations) {
    iteration++;
    const layout = {
      id: layouts.length + 1,
      width: binWidth,
      height: binHeight,
      pieces: []
    };

    const placedPolygons = []; // Polygons at their placed positions (for collision checking)
    const placedIndices = new Set();

    // Try to place each remaining piece
    for (let i = 0; i < remainingPieces.length; i++) {
      if (placedIndices.has(i)) continue;

      const piece = remainingPieces[i];
      const piecePolygon = getPiecePolygon(piece);

      // Determine allowed rotations based on grain
      // For trapezoids with horizontal grain, allow 0 and 180 for interlocking
      let allowedRotations = [...rotations];
      if (piece.grainDirection === 'horizontal' || piece.grainDirection === 'H') {
        allowedRotations = [0, 180]; // These preserve horizontal grain
      } else if (piece.grainDirection === 'vertical' || piece.grainDirection === 'V') {
        allowedRotations = [0, 180]; // These preserve vertical grain
      } else if (piece.grainDirection && piece.grainDirection !== 'none') {
        allowedRotations = [0, 180];
      }

      let bestPlacement = null;
      let bestRotation = 0;
      let bestNormalizedPoly = null;
      let bestScore = Infinity; // Lower is better (y * 1000 + x)

      // Try ALL rotations and find the BEST placement across all of them
      for (const angle of allowedRotations) {
        const rotatedPoly = angle === 0 ? piecePolygon : rotatePolygon(piecePolygon, angle);
        const normalized = normalizePolygon(rotatedPoly);

        // Skip if piece is too large for bin in this orientation
        if (normalized.width + spacing > binWidth || normalized.height + spacing > binHeight) {
          continue;
        }

        // Find best position for this rotation using grid search
        const position = findBestGridPosition(
          normalized,
          placedPolygons,
          binWidth,
          binHeight,
          spacing
        );

        if (position) {
          // Score: prefer lower y, then lower x
          const score = position.y * 10000 + position.x;
          if (score < bestScore) {
            bestScore = score;
            bestPlacement = position;
            bestRotation = angle;
            bestNormalizedPoly = normalized;
          }
        }
      }

      if (bestPlacement && bestNormalizedPoly) {
        // Store the placed polygon at its actual position for collision checking
        const placedPoly = translatePolygon(bestNormalizedPoly.polygon, bestPlacement.x, bestPlacement.y);
        placedPolygons.push(placedPoly);

        layout.pieces.push({
          ...piece,
          x: bestPlacement.x,
          y: bestPlacement.y,
          width: bestNormalizedPoly.width,
          height: bestNormalizedPoly.height,
          vertices: bestNormalizedPoly.polygon,
          rotated: bestRotation !== 0,
          rotationAngle: bestRotation
        });

        placedIndices.add(i);
        console.log(`Placed piece ${i} (${piece.name || 'unnamed'}) at (${bestPlacement.x.toFixed(1)}, ${bestPlacement.y.toFixed(1)}) rotation ${bestRotation}°`);
      }
    }

    // Remove placed pieces from remaining
    remainingPieces = remainingPieces.filter((_, i) => !placedIndices.has(i));

    if (layout.pieces.length > 0) {
      layouts.push(layout);
      console.log(`Sheet ${layout.id}: placed ${layout.pieces.length} pieces`);
    } else if (remainingPieces.length > 0) {
      // No pieces could be placed on this sheet - but we still have pieces!
      // This means pieces might be too large, or there's a bug
      // Try placing just the FIRST remaining piece on a fresh sheet
      const firstPiece = remainingPieces[0];
      const piecePolygon = getPiecePolygon(firstPiece);

      console.warn(`Sheet ${layout.id}: Could not place pieces normally. Attempting single piece placement for: ${firstPiece.name || 'piece'} (${firstPiece.width.toFixed(1)}x${firstPiece.height.toFixed(1)})`);

      // Try each rotation for this single piece
      let placed = false;
      for (const angle of [0, 90, 180, 270]) {
        if (placed) break;

        const rotatedPoly = angle === 0 ? piecePolygon : rotatePolygon(piecePolygon, angle);
        const normalized = normalizePolygon(rotatedPoly);

        // Check if it fits at all
        if (normalized.width + spacing <= binWidth && normalized.height + spacing <= binHeight) {
          layout.pieces.push({
            ...firstPiece,
            x: spacing,
            y: spacing,
            width: normalized.width,
            height: normalized.height,
            vertices: normalized.polygon,
            rotated: angle !== 0,
            rotationAngle: angle
          });

          layouts.push(layout);
          remainingPieces = remainingPieces.slice(1);
          placed = true;
          console.log(`Force-placed ${firstPiece.name || 'piece'} on Sheet ${layout.id} with rotation ${angle}°`);
        }
      }

      if (!placed) {
        // Piece truly doesn't fit - mark as unplaceable and continue
        console.error(`Piece ${firstPiece.name || 'unnamed'} (${firstPiece.width.toFixed(1)}x${firstPiece.height.toFixed(1)}) is too large for sheet (${binWidth}x${binHeight})`);
        remainingPieces = remainingPieces.slice(1);
        // Don't break - continue with other pieces
      }
    }
  }

  if (iteration >= maxIterations) {
    console.error(`Polygon nesting hit iteration limit (${maxIterations})`);
  }

  console.log(`Polygon nesting complete: ${layouts.length} sheets, ${remainingPieces.length} unplaced`);

  return {
    layouts,
    unplacedPieces: remainingPieces,
    unplacedCount: remainingPieces.length
  };
};

/**
 * Find best grid position for a normalized polygon
 * Tests all grid positions and returns the best (lowest y, then lowest x)
 */
const findBestGridPosition = (normalized, placedPolygons, binWidth, binHeight, spacing) => {
  // Use adaptive grid step - finer for smaller pieces
  const pieceSize = Math.min(normalized.width, normalized.height);
  const gridStep = Math.max(Math.min(spacing * 2, pieceSize / 4), 0.25);

  let bestPosition = null;
  let bestScore = Infinity;

  for (let y = spacing; y <= binHeight - normalized.height - spacing; y += gridStep) {
    for (let x = spacing; x <= binWidth - normalized.width - spacing; x += gridStep) {
      // Translate polygon to test position
      const testPoly = translatePolygon(normalized.polygon, x, y);

      // Check bounds (with spacing margin)
      const testBounds = getPolygonBounds(testPoly);
      if (testBounds.x < 0 || testBounds.y < 0 ||
          testBounds.x + testBounds.width > binWidth ||
          testBounds.y + testBounds.height > binHeight) {
        continue;
      }

      // Check collision with all placed pieces
      let collision = false;
      for (const placed of placedPolygons) {
        // Check direct intersection
        if (polygonsIntersect(testPoly, placed)) {
          collision = true;
          break;
        }

        // Check with spacing buffer
        if (spacing > 0) {
          const expandedTest = offsetPolygon(testPoly, spacing / 2);
          const expandedPlaced = offsetPolygon(placed, spacing / 2);
          if (expandedTest && expandedPlaced && polygonsIntersect(expandedTest, expandedPlaced)) {
            collision = true;
            break;
          }
        }
      }

      if (!collision) {
        const score = y * 10000 + x;
        if (score < bestScore) {
          bestScore = score;
          bestPosition = { x, y };
        }
        // Don't break - continue searching for better positions at same y
        // But we can skip to next y level after finding something at this y
        break;
      }
    }

    // Early exit: if we found a position and moved to next y level, we're done
    // (since positions at higher y will have worse scores)
    if (bestPosition && y > bestPosition.y) {
      break;
    }
  }

  return bestPosition;
};

/**
 * Offset polygon (expand or shrink) using Clipper
 */
const offsetPolygon = (polygon, delta) => {
  if (Math.abs(delta) < 0.001) return polygon;

  const clipperPoly = toClipperPolygon(polygon);

  const co = new ClipperLib.ClipperOffset();
  const solution = new ClipperLib.Paths();

  co.AddPath(clipperPoly, ClipperLib.JoinType.jtMiter, ClipperLib.EndType.etClosedPolygon);
  co.Execute(solution, delta * CLIPPER_SCALE);

  if (solution.length === 0) return null;

  return fromClipperPolygon(solution[0]);
};

export default {
  polygonArea,
  getPolygonBounds,
  pointInPolygon,
  rotatePolygon,
  translatePolygon,
  normalizePolygon,
  noFitPolygon,
  innerFitPolygon,
  polygonsIntersect,
  nestPolygons,
  getRectangleVertices,
  getPiecePolygon
};
